// Static catalogue of every feature the MCP server recognises. Kept in sync by hand
// with the descriptions in docs/permissions.md and the generated metadata under
// packages/mcp-server/src/generated/. If a new module is added to Sage 300 there,
// mirror it here so the admin form surfaces it.

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

export interface ModuleFeatureGroup {
  code: string;
  label: MessageDescriptor;
  // A given module may expose only a subset of surfaces. `undefined` means the
  // matching feature doesn't exist for this module (e.g. Project & Job Costing
  // has no Web API tools, Tax has no `.write`, User Portal has no SQL bucket).
  sqlRead?: string;
  webApiRead?: string;
  webApiWrite?: string;
  webApiPost?: string;
}

// Cross-module features that don't belong to a single Sage module.
export const EXTRA_FEATURES = {
  discovery: {
    id: "discovery",
    label: msg`Discovery tools (search_entities, describe_entity, resolve_question)`,
  },
  dbCross: {
    id: "db.cross.read",
    label: msg`Cross-module SQL joins (sage300_db_cross_query)`,
  },
  advancedDocumentScripts: {
    id: "advanced_document_scripts",
    label: msg`Advanced Windows-isolated document scripts (approval required per run)`,
  },
} as const;

export const MODULE_GROUPS: ModuleFeatureGroup[] = [
  {
    code: "AP",
    label: msg`Accounts Payable`,
    sqlRead: "db.ap.read",
    webApiRead: "accountsPayable.read",
    webApiWrite: "accountsPayable.write",
    webApiPost: "accountsPayable.post",
  },
  {
    code: "AR",
    label: msg`Accounts Receivable`,
    sqlRead: "db.ar.read",
    webApiRead: "accountsReceivable.read",
    webApiWrite: "accountsReceivable.write",
    webApiPost: "accountsReceivable.post",
  },
  {
    code: "GL",
    label: msg`General Ledger`,
    sqlRead: "db.gl.read",
    webApiRead: "generalLedger.read",
    webApiWrite: "generalLedger.write",
    webApiPost: "generalLedger.post",
  },
  {
    code: "PO",
    label: msg`Purchase Orders`,
    sqlRead: "db.po.read",
    webApiRead: "purchasing.read",
    webApiWrite: "purchasing.write",
    webApiPost: "purchasing.post",
  },
  {
    code: "OE",
    label: msg`Order Entry`,
    sqlRead: "db.oe.read",
    webApiRead: "orderEntry.read",
    webApiWrite: "orderEntry.write",
  },
  {
    code: "IC",
    label: msg`Inventory Control`,
    sqlRead: "db.ic.read",
    webApiRead: "inventory.read",
    webApiWrite: "inventory.write",
    webApiPost: "inventory.post",
  },
  {
    code: "PM",
    label: msg`Project & Job Costing`,
    sqlRead: "db.pm.read",
  },
  {
    code: "BK",
    label: msg`Bank Services`,
    sqlRead: "db.bk.read",
    webApiRead: "bankServices.read",
    webApiWrite: "bankServices.write",
  },
  {
    code: "CS",
    label: msg`Common Services`,
    sqlRead: "db.cs.read",
    webApiRead: "commonServices.read",
    webApiWrite: "commonServices.write",
  },
  {
    code: "CP",
    label: msg`Canadian Payroll`,
    sqlRead: "db.cp.read",
    webApiRead: "canadianPayroll.read",
    webApiWrite: "canadianPayroll.write",
  },
  {
    code: "AS",
    label: msg`Administrative Services`,
    sqlRead: "db.as.read",
  },
  {
    code: "GP",
    label: msg`G/L Subledger Services`,
    sqlRead: "db.gp.read",
  },
  {
    code: "FA",
    label: msg`Sage Fixed Assets Integration`,
    sqlRead: "db.fa.read",
  },
  {
    code: "EW",
    label: msg`Sage CRM Integration`,
    sqlRead: "db.ew.read",
  },
  {
    code: "MT",
    label: msg`Multiple Contacts`,
    sqlRead: "db.mt.read",
    webApiRead: "multipleContacts.read",
    webApiWrite: "multipleContacts.write",
  },
  {
    code: "CT",
    label: msg`Cdn PR Tax Update`,
    sqlRead: "db.ct.read",
  },
  {
    code: "TX",
    label: msg`Tax Services`,
    webApiRead: "tax.read",
  },
  {
    code: "UP",
    label: msg`User Portal`,
    webApiRead: "up.read",
    webApiWrite: "up.write",
  },
];

// Which surface of a module a feature id opens. Kept next to the groups above so
// a read-only listing (the licence feature dialog) can label an id without
// re-deriving the module/surface split at the call site.
const SURFACE_LABELS = {
  sqlRead: msg`SQL read`,
  webApiRead: msg`Web API read`,
  webApiWrite: msg`Web API write`,
  webApiPost: msg`Web API post`,
} as const;

export interface FeatureCatalogEntry {
  id: string;
  /** Module label, or the cross-module label for the extras. */
  label: MessageDescriptor;
  /** Surface opened on that module; absent for the cross-module extras. */
  surface?: MessageDescriptor;
}

export const FEATURE_CATALOG: ReadonlyMap<string, FeatureCatalogEntry> = (() => {
  const map = new Map<string, FeatureCatalogEntry>();
  for (const extra of Object.values(EXTRA_FEATURES)) {
    map.set(extra.id, { id: extra.id, label: extra.label });
  }
  for (const g of MODULE_GROUPS) {
    for (const surface of ["sqlRead", "webApiRead", "webApiWrite", "webApiPost"] as const) {
      const id = g[surface];
      if (id) map.set(id, { id, label: g.label, surface: SURFACE_LABELS[surface] });
    }
  }
  return map;
})();

// Flat list of every recognised feature id. Useful for validation, presets and
// the "select everything" convenience button.
export const ALL_FEATURE_IDS: string[] = (() => {
  const ids: string[] = [
    EXTRA_FEATURES.discovery.id,
    EXTRA_FEATURES.dbCross.id,
    EXTRA_FEATURES.advancedDocumentScripts.id,
  ];
  for (const g of MODULE_GROUPS) {
    if (g.sqlRead) ids.push(g.sqlRead);
    if (g.webApiRead) ids.push(g.webApiRead);
    if (g.webApiWrite) ids.push(g.webApiWrite);
    if (g.webApiPost) ids.push(g.webApiPost);
  }
  return ids;
})();

export const DEFAULT_FEATURES: string[] = [
  EXTRA_FEATURES.discovery.id,
  "accountsPayable.read",
  "accountsReceivable.read",
];
